// payoutmanagement/PayoutList.jsx — 정산입금 목록 테이블 (날짜별 정렬)
// 각 행 클릭 시 계좌/세금정보 편집 모달 열림

import React from 'react';
import { toDateString } from '../../../utils/date';
import { stdTableWrap, stdTable, stdTh, stdCell } from '../adminlayout/designStandard';

// ── 숫자 포맷 ─────────────────────────────────────────────────────────────────
function fmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ko-KR');
}

// ── 날짜 포맷 ─────────────────────────────────────────────────────────────────
function fmtDate(v) { return v ? toDateString(v) : '-'; }

// ── 요약 텍스트 ─────────────────────────────────────────────────────────────
function bankSummary(row) {
  if (!row.bank_name) return '미등록';
  return `${row.bank_name} ${row.account_number || ''} (${row.account_holder || ''})`;
}
function taxSummary(row) {
  if (!row.tax_name) return '미등록';
  if (row.pay_tax_type === 'business') {
    return `${row.tax_name} / 사업자 ${row.business_registration_number || ''} / 부가세 가산`;
  }
  return `${row.tax_name} / ${row.resident_number_masked || '***'} / ${row.tax_rate ?? 3.3}%`;
}

// 사업자(부가세 가산) 배지 — 스위치 ON + 사업자 유형 행에서만 렌더
const bizBadgeStyle = {
  fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 8,
  background: 'rgba(37,99,235,0.12)', color: '#2563eb', whiteSpace: 'nowrap',
};

export function PayoutList({ listData, listLoading, openEditModal, selectedDate, isMobile, taxTypeEnabled = false }) {
  // 선택된 날짜가 있으면 해당 날짜만 필터
  const filtered = selectedDate
    ? listData.filter((r) => toDateString(r.settlement_date) === selectedDate)
    : listData;

  if (listLoading) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>불러오는 중...</div>;
  }

  if (filtered.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        {selectedDate ? `${selectedDate} 정산 데이터 없음` : '이 달 정산 데이터 없음'}
      </div>
    );
  }

  // ── 모바일: 카드 레이아웃 ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0' }}>
        {filtered.map((row) => {
          const hasBankInfo = !!row.bank_name;
          const hasTaxInfo  = !!row.tax_name;
          return (
            <div
              key={row.id}
              onClick={() => openEditModal(row)}
              style={{
                border: '1px solid var(--border)', borderRadius: 10,
                padding: '12px 14px', background: 'var(--bg-card)',
                cursor: 'pointer', transition: 'box-shadow 0.15s',
              }}
            >
              {/* 날짜 + 금액 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(row.settlement_date)}</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#059669' }}>{fmt(row.amount)}원</span>
              </div>
              {/* 마케터 이름 (+사업자 배지 / 실지급액 — 스위치 ON 시) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{row.user_name || '-'}</span>
                {taxTypeEnabled && row.pay_tax_type === 'business' && <span style={bizBadgeStyle}>사업자 +부가세</span>}
                {taxTypeEnabled && row.net_amount != null && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>실지급 {fmt(row.net_amount)}원</span>
                )}
              </div>
              {/* 계좌 */}
              <div style={{ fontSize: 11, color: hasBankInfo ? 'var(--text-sub)' : '#dc2626', marginBottom: 2 }}>
                계좌: {bankSummary(row)}
              </div>
              {/* 세금정보 */}
              <div style={{ fontSize: 11, color: hasTaxInfo ? 'var(--text-sub)' : '#dc2626' }}>
                세금: {taxSummary(row)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── PC: 테이블 레이아웃 — 디자인 표준 표 패턴 (2026-08-27 통일) ────────────
  const missSt = { color: '#dc2626', fontWeight: 600 };

  return (
    <div style={stdTableWrap}>
      <table style={stdTable}>
        <thead>
          <tr>
            <th style={stdTh}>정산일</th>
            <th style={stdTh}>마케터</th>
            <th style={{ ...stdTh, textAlign: 'right' }}>금액</th>
            <th style={stdTh}>계좌정보</th>
            <th style={stdTh}>세금정보</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row, rowIndex) => {
            const hasBankInfo = !!row.bank_name;
            const hasTaxInfo  = !!row.tax_name;
            const tdSt = stdCell(rowIndex, filtered.length);
            return (
              <tr
                key={row.id}
                onClick={() => openEditModal(row)}
                style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37,99,235,0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
              >
                <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{fmtDate(row.settlement_date)}</td>
                <td style={{ ...tdSt, fontWeight: 700, color: 'var(--text)' }}>
                  {row.user_name || '-'}
                  {taxTypeEnabled && row.pay_tax_type === 'business' && (
                    <span style={{ ...bizBadgeStyle, marginLeft: 6 }}>사업자 +부가세</span>
                  )}
                </td>
                <td style={{ ...tdSt, textAlign: 'right', fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>
                  {fmt(row.amount)}원
                  {taxTypeEnabled && row.net_amount != null && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>실지급 {fmt(row.net_amount)}원</div>
                  )}
                </td>
                <td style={{ ...tdSt, ...(hasBankInfo ? {} : missSt), fontSize: 12 }}>{bankSummary(row)}</td>
                <td style={{ ...tdSt, ...(hasTaxInfo ? {} : missSt), fontSize: 12 }}>{taxSummary(row)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
